/**
 * The six mute duration labels and the "Muted until 9:00 PM" header string,
 * as one pure module. No component in `chatDetails/` formats a mute time
 * inline; every one of them calls in here instead.
 *
 * `typeChatMuteDuration` is declared locally rather than imported from the
 * SDK: there it lives nested inside `typeChatMuteSetBody` and is not
 * separately exported.
 */

export type typeChatMuteDuration =
  | "fifteenMinutes"
  | "oneHour"
  | "threeHours"
  | "eightHours"
  | "twentyFourHours"
  | "indefinite";

export const MUTE_DURATIONS: readonly {
  label: string;
  value: typeChatMuteDuration;
}[] = [
  { label: "15 minutes", value: "fifteenMinutes" },
  { label: "1 hour", value: "oneHour" },
  { label: "3 hours", value: "threeHours" },
  { label: "8 hours", value: "eightHours" },
  { label: "24 hours", value: "twentyFourHours" },
  { label: "Until I turn it back on", value: "indefinite" },
];

/** `Muted until 9:00 PM`, on the device's own 12-hour clock. */
export function formatMutedUntil(mutedUntilIso: string, now: Date): string {
  const time = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(mutedUntilIso));
  // `now` is accepted for signature symmetry with the rest of this module
  // and so a future caller can pass a fixed clock in a test without this
  // function reaching for `Date.now()` itself. It plays no role in the
  // 12-hour clock format itself.
  void now;
  return `Muted until ${time}`;
}

/**
 * The header's own mute line, or `null` when nothing should render.
 *
 * A `timed` mute whose `mutedUntilIso` is missing, or already at or before
 * `now`, returns `null` so a stale mute never shows a time in the past.
 */
export function muteHeaderLabel(
  state: "off" | "timed" | "indefinite",
  mutedUntilIso: string | null | undefined,
  now: Date,
): string | null {
  if (state === "off") return null;
  if (state === "indefinite") return "Muted";

  if (!mutedUntilIso) return null;
  const until = new Date(mutedUntilIso);
  if (until.getTime() <= now.getTime()) return null;

  return formatMutedUntil(mutedUntilIso, now);
}
